/**
 * Events Landing Page
 *
 * SEO-optimized landing page targeting event organizers.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSEO, useHreflang, useStructuredData, SITE_CONFIG } from '../../lib/seo';
import PublicLayout from '../../components/layout/PublicLayout';

export default function EventsPage() {
  const { t, i18n } = useTranslation('events');

  useSEO({
    title: 'Event Registration & Live Polling | Conferences, Meetups & Webinars | xtFORM',
    description: 'Create event registration forms, live audience polls, and interactive Q&A sessions. Perfect for conferences, workshops, webinars, and team events. Free to use.',
    keywords: 'event registration forms, live polling, audience engagement, conference tools, webinar polls, Q&A forms, event feedback, workshop registration, meetup forms',
    url: `${SITE_CONFIG.domain}/events`,
    locale: i18n.language,
  });

  useHreflang('/events');

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': 'Event Registration & Live Polling',
    'description': 'Interactive forms and polls for event organizers.',
    'url': `${SITE_CONFIG.domain}/events`,
    'mainEntity': {
      '@type': 'SoftwareApplication',
      'name': 'xtFORM for Events',
      'applicationCategory': 'EventApplication',
      'operatingSystem': 'Web',
      'offers': {
        '@type': 'Offer',
        'price': '0',
        'priceCurrency': 'USD',
      },
    },
  };

  useStructuredData(structuredData, 'events-page');

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-purple-50 via-white to-pink-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full text-purple-700 text-sm font-medium mb-6">
              <span className="text-lg">🎪</span>
              {t('hero.badge')}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('hero.title')}
              <span className="text-purple-600">{t('hero.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="px-8 py-4 bg-purple-600 text-white rounded-xl font-semibold text-lg hover:bg-purple-700 transition-colors shadow-lg shadow-purple-500/30"
              >
                {t('hero.ctaPrimary')}
              </Link>
              <Link
                to="/demo/poll"
                className="px-8 py-4 border-2 border-purple-600 text-purple-600 rounded-xl font-semibold text-lg hover:bg-purple-50 transition-colors"
              >
                {t('hero.ctaSecondary')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Event Lifecycle */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('eventLifecycle.title')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('eventLifecycle.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Before */}
            <div className="relative">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold text-lg z-10">
                1
              </div>
              <div className="pt-16 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 h-full">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">{t('eventLifecycle.stages.before.title')}</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-purple-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">{t('eventLifecycle.stages.before.items.registration.title')}</strong>
                      <p className="text-gray-600 text-sm">{t('eventLifecycle.stages.before.items.registration.description')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">{t('eventLifecycle.stages.before.items.speakers.title')}</strong>
                      <p className="text-gray-600 text-sm">{t('eventLifecycle.stages.before.items.speakers.description')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">{t('eventLifecycle.stages.before.items.surveys.title')}</strong>
                      <p className="text-gray-600 text-sm">{t('eventLifecycle.stages.before.items.surveys.description')}</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* During */}
            <div className="relative">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg z-10">
                2
              </div>
              <div className="pt-16 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl p-8 text-white h-full">
                <h3 className="text-xl font-semibold mb-4 text-center">{t('eventLifecycle.stages.during.title')}</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-purple-200 mt-1">✓</span>
                    <div>
                      <strong>{t('eventLifecycle.stages.during.items.polls.title')}</strong>
                      <p className="text-purple-200 text-sm">{t('eventLifecycle.stages.during.items.polls.description')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-200 mt-1">✓</span>
                    <div>
                      <strong>{t('eventLifecycle.stages.during.items.qa.title')}</strong>
                      <p className="text-purple-200 text-sm">{t('eventLifecycle.stages.during.items.qa.description')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-200 mt-1">✓</span>
                    <div>
                      <strong>{t('eventLifecycle.stages.during.items.quiz.title')}</strong>
                      <p className="text-purple-200 text-sm">{t('eventLifecycle.stages.during.items.quiz.description')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-200 mt-1">✓</span>
                    <div>
                      <strong>{t('eventLifecycle.stages.during.items.wordCloud.title')}</strong>
                      <p className="text-purple-200 text-sm">{t('eventLifecycle.stages.during.items.wordCloud.description')}</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* After */}
            <div className="relative">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold text-lg z-10">
                3
              </div>
              <div className="pt-16 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 h-full">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">{t('eventLifecycle.stages.after.title')}</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-purple-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">{t('eventLifecycle.stages.after.items.feedback.title')}</strong>
                      <p className="text-gray-600 text-sm">{t('eventLifecycle.stages.after.items.feedback.description')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">{t('eventLifecycle.stages.after.items.export.title')}</strong>
                      <p className="text-gray-600 text-sm">{t('eventLifecycle.stages.after.items.export.description')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">{t('eventLifecycle.stages.after.items.followup.title')}</strong>
                      <p className="text-gray-600 text-sm">{t('eventLifecycle.stages.after.items.followup.description')}</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('eventTypes.title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🎤</div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('eventTypes.types.conferences.title')}</h3>
              <p className="text-gray-600 text-sm">{t('eventTypes.types.conferences.description')}</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">💻</div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('eventTypes.types.webinars.title')}</h3>
              <p className="text-gray-600 text-sm">{t('eventTypes.types.webinars.description')}</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🛠️</div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('eventTypes.types.workshops.title')}</h3>
              <p className="text-gray-600 text-sm">{t('eventTypes.types.workshops.description')}</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🏢</div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('eventTypes.types.team.title')}</h3>
              <p className="text-gray-600 text-sm">{t('eventTypes.types.team.description')}</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🎓</div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('eventTypes.types.training.title')}</h3>
              <p className="text-gray-600 text-sm">{t('eventTypes.types.training.description')}</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🤝</div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('eventTypes.types.meetups.title')}</h3>
              <p className="text-gray-600 text-sm">{t('eventTypes.types.meetups.description')}</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🎉</div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('eventTypes.types.social.title')}</h3>
              <p className="text-gray-600 text-sm">{t('eventTypes.types.social.description')}</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">📚</div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('eventTypes.types.academic.title')}</h3>
              <p className="text-gray-600 text-sm">{t('eventTypes.types.academic.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* How Live Polling Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {t('livePolling.title')}
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 text-purple-600 font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('livePolling.steps.create.title')}</h3>
                    <p className="text-gray-600">{t('livePolling.steps.create.description')}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 text-purple-600 font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('livePolling.steps.share.title')}</h3>
                    <p className="text-gray-600">{t('livePolling.steps.share.description')}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 text-purple-600 font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('livePolling.steps.watch.title')}</h3>
                    <p className="text-gray-600">{t('livePolling.steps.watch.description')}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl p-8">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="text-center mb-6">
                  <div className="text-sm text-gray-500 mb-2">{t('livePolling.demo.joinAt')}</div>
                  <div className="text-2xl font-bold text-purple-600">xtform.com/play</div>
                </div>
                <div className="border-t border-gray-100 pt-6">
                  <div className="text-center mb-4">
                    <div className="text-sm text-gray-500 mb-1">{t('livePolling.demo.gamePin')}</div>
                    <div className="text-4xl font-mono font-bold text-gray-900 tracking-widest">283 194</div>
                  </div>
                  <div className="flex justify-center">
                    <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                      <span className="text-4xl">📱</span>
                    </div>
                  </div>
                  <p className="text-center text-sm text-gray-500 mt-4">
                    {t('livePolling.demo.scanQr')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {t('cta.title')}
          </h2>
          <p className="text-purple-100 text-lg mb-8">
            {t('cta.subtitle')}
          </p>
          <Link
            to="/register"
            className="inline-block px-8 py-4 bg-white text-purple-600 rounded-xl font-semibold text-lg hover:bg-purple-50 transition-colors"
          >
            {t('cta.button')}
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
